"use client"

import { useState, useContext } from "react"
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  Fade,
  Slide,
} from "@mui/material"
import { alpha, createTheme } from "@mui/material/styles"
import { Mail, Send, UserPlus } from "lucide-react"
import axiosInstance from "../../utils/axiosInstance"
import { useOrganization, authorizeContext } from "../../hooks/Authorize"

// It's good practice to define the theme where it's used or globally,
// but since AgentDashboard already has a similar theme,
// we can define a minimal one here if specific overrides are needed,
// or rely on the Dialog's theme context.
// For simplicity, we'll use a minimal theme for styling consistency if needed,
// but often Dialogs inherit well.
const formTheme = createTheme({
  palette: {
    mode: "dark", // Assuming the dialog is in a dark context
    primary: {
      main: "#3b82f6",
    },
    secondary: {
      main: "#10b981",
    },
    background: {
      paper: "#1e1e1e", // Dialog content background
      default: "#121212",
    },
    text: {
      primary: "#f3f4f6",
      secondary: "#9ca3af",
    },
    success: {
      main: "#10b981",
    },
    error: {
      main: "#ef4444",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "rgba(255, 255, 255, 0.23)",
            },
            "&:hover fieldset": {
              borderColor: "rgba(255, 255, 255, 0.5)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#3b82f6",
            },
            "& input": {
              color: "#f3f4f6",
            },
            "& input::placeholder": {
              color: "#9ca3af",
              opacity: 1,
            },
          },
          "& .MuiInputLabel-root": {
            color: "#9ca3af",
            "&.Mui-focused": {
              color: "#3b82f6",
            },
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
})

const InviteAgentForm = ({ onSuccess }) => {
  const [email, setEmail] = useState("")
  const [message, setMessage] = useState("")
  const [error, setError] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const authContextDirect = useContext(authorizeContext)
  const currentOrganization = authContextDirect?.currentOrganization

  const handleSubmit = async (e) => {
    e.preventDefault()
    setMessage("")
    setError("")
    setIsLoading(true)

    if (!currentOrganization || !currentOrganization._id) {
      setError("No organization selected. Please ensure an organization is active.")
      setIsLoading(false)
      return
    }

    if (!email.trim()) {
      setError("Email address is required.")
      setIsLoading(false)
      return
    }

    try {
      const frontendUrl = window.location.origin;
      const response = await axiosInstance.post("/api/agents/invite", {
        email: email,
        organizationId: currentOrganization._id,
        frontendUrl: frontendUrl,
      })

      if (response.data.success) {
        setMessage(response.data.message || "Invitation sent successfully!")
        setEmail("")
        if (onSuccess && typeof onSuccess === "function") {
          setTimeout(onSuccess, 1500) // Call onSuccess after a short delay to show message
        }
      } else {
        setError(response.data.error || "Failed to send invitation.")
      }
    } catch (err) {
      console.error("Invite Agent Error:", err)
      setError(err.response?.data?.error || "An error occurred. Please try again.")
    }
    setIsLoading(false)
  }

  // Use a fragment or a simple Box if ThemeProvider is removed at the top level
  // The Dialog in AgentDashboard will provide the theme.
  return (
    // The Paper component can be the root for the form's content structure.
    // Styling will be largely inherited or controlled by the Dialog.
    <Paper
      elevation={0} // Dialogs often handle their own elevation/shadow.
      sx={{
        p: { xs: 2, sm: 3 }, // Responsive padding
        bgcolor: "transparent", // Let Dialog control background
        // maxWidth: 500, // Max width can be controlled by Dialog's maxWidth prop
        // width: "100%", // Will fill the Dialog content area
        border: "none", // Remove border if Dialog provides it
        // overflow: "hidden", // Not necessary if content is well-contained
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(formTheme.palette.primary.main, 0.1), // Use local formTheme
            mr: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <UserPlus size={24} color={formTheme.palette.primary.main} />
        </Box>
        <Box>
          <Typography
            variant="h6" // Adjusted for Dialog context
            sx={{
              fontWeight: 600, // Slightly reduced for dialog
              color: "text.primary",
              mb: 0.25,
            }}
          >
            Invite New Agent
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            Send an invitation to join your organization
          </Typography>
        </Box>
      </Box>

      {/* Form */}
      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          type="email"
          label="Agent's Email Address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter agent's email address"
          required
          disabled={isLoading}
          sx={{
            mb: 2.5, // Adjusted margin
            "& .MuiOutlinedInput-root": {
              // bgcolor: alpha(formTheme.palette.background.default, 0.5),
              "&:hover": {
                // bgcolor: alpha(formTheme.palette.background.default, 0.7),
              },
              "&.Mui-focused": {
                // bgcolor: alpha(formTheme.palette.background.default, 0.8),
                boxShadow: `0 0 0 2px ${alpha(formTheme.palette.primary.main, 0.25)}`,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Mail size={20} color={formTheme.palette.text.secondary} />
              </InputAdornment>
            ),
          }}
          variant="outlined" // Ensure variant is specified
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isLoading || !email.trim()}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Send size={20} />}
          sx={{
            py: 1.25, // Adjusted padding
            fontSize: "0.95rem", // Adjusted font size
            // fontWeight: 600, // Already in theme
            // background: `linear-gradient(45deg, ${formTheme.palette.primary.main}, ${alpha(formTheme.palette.primary.main, 0.8)})`,
            // boxShadow: `0 4px 12px ${alpha(formTheme.palette.primary.main, 0.3)}`,
            transition: "all 0.3s ease",
            "&:hover": {
              // background: `linear-gradient(45deg, ${alpha(formTheme.palette.primary.main, 0.9)}, ${formTheme.palette.primary.main})`,
              // boxShadow: `0 6px 16px ${alpha(formTheme.palette.primary.main, 0.4)}`,
              transform: "translateY(-1px)", // Subtle hover effect
            },
            "&:disabled": {
              // background: alpha(formTheme.palette.action.disabled, 0.3),
              // color: alpha(formTheme.palette.text.disabled, 0.5),
              // boxShadow: "none",
              // transform: "none",
            },
          }}
        >
          {isLoading ? "Sending..." : "Send Invitation"}
        </Button>
      </Box>

      {/* Messages */}
      <Box sx={{ mt: 2.5, minHeight: '48px' }}> {/* Reserve space for messages */}
        {message && (
          <Fade in timeout={400}>
            <Alert
              severity="success"
              sx={{
                // "& .MuiAlert-icon": { // Already themed
                //   color: formTheme.palette.success.main,
                // },
              }}
            >
              {message}
            </Alert>
          </Fade>
        )}
        {error && (
          <Fade in timeout={400}>
            <Alert
              severity="error"
              sx={{
                // "& .MuiAlert-icon": { // Already themed
                //   color: formTheme.palette.error.main,
                // },
              }}
            >
              {error}
            </Alert>
          </Fade>
        )}
      </Box>

      {/* Organization Info */}
      {currentOrganization && (
        <Box
          sx={{
            mt: 2,
            p: 1.5, // Adjusted padding
            borderRadius: 1.5, // Adjusted border radius
            bgcolor: alpha(formTheme.palette.primary.main, 0.05), // More subtle background
            border: `1px solid ${alpha(formTheme.palette.primary.main, 0.15)}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "block",
              mb: 0.5,
            }}
          >
            Inviting to:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.primary",
              fontWeight: 500,
            }}
          >
            {currentOrganization.name || currentOrganization._id}
          </Typography>
        </Box>
      )}
    </Paper>
    // Removed ThemeProvider, CssBaseline, outer Box, Fade, Slide and global styles
    // The Dialog in AgentDashboard.jsx will now control the modal presentation and theming.
  )
}

export default InviteAgentForm
